import {
  getGetApiInstitutionQueryOptions,
  useGetApiInstitutionFilterFields,
  type InstitutionViewDTO,
} from '@smartsash/api-client';
import type { TableFilterFieldOverrides } from '@smartsash/ui';
import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import {
  INSTITUTES_CITY_FIELD,
  institutionsColumnText,
  institutionsQueryParams,
  type InstitutesSearch,
} from './institutionsModel';

import { DataScreenFilter, filterColumnsFromText } from '@/ui/shared/TableFilter';

/**
 * Above this many distinct values the vocabulary endpoint stops returning them.
 *
 * It answers `truncated: true` with `values: null` instead, because a checkbox list of hundreds of
 * cities is not a control anyone can use. The field then becomes free text, which reaches the same
 * `city` parameter with the one value the user typed.
 */
const VOCABULARY_FIELDS = INSTITUTES_CITY_FIELD;

/**
 * The filter builder for the Institutes list (HS-869 + the filtering-foundation follow-up).
 *
 * The rows come from the screen's own cached query — `institutionsQueryParams` builds it identically
 * here and in `institutionsScreen`. City is the one field the endpoint accepts, and its vocabulary
 * cannot come from the loaded rows: server-side pagination means those hold ten of the cities, so
 * the count the design shows beside the field would be wrong. `/filter-fields` scopes it to the
 * institutions the user may see instead, and falls back to free text once the vocabulary is too
 * large for the endpoint to enumerate.
 */
export function InstitutionsFilter({ search }: { search: InstitutesSearch }) {
  const { t } = useTranslation();
  const { data } = useQuery(getGetApiInstitutionQueryOptions(institutionsQueryParams(search)));
  const { data: vocabulary } = useGetApiInstitutionFilterFields({ fields: VOCABULARY_FIELDS });
  const cityField = vocabulary?.fields?.find((field) => field.id === INSTITUTES_CITY_FIELD);
  const cityOptions = useMemo(
    () =>
      (cityField?.values ?? [])
        .filter((value) => value.value != null && value.value !== '')
        .map((value) => ({ value: value.value ?? '', label: value.label ?? value.value ?? '' })),
    [cityField],
  );

  const columns = useMemo(
    () =>
      filterColumnsFromText<InstitutionViewDTO>({ ...institutionsColumnText }, (id) =>
        t(`institutes.columns.${id as 'city'}`),
      ),
    [t],
  );

  const overrides = useMemo<TableFilterFieldOverrides>(
    () => ({
      types: cityField?.truncated === true ? { [INSTITUTES_CITY_FIELD]: 'text' } : {},
      options: cityField?.truncated === true ? {} : { [INSTITUTES_CITY_FIELD]: cityOptions },
    }),
    [cityField, cityOptions],
  );

  return <DataScreenFilter columns={columns} rows={data?.items ?? []} overrides={overrides} />;
}
